import React, { useState, useEffect, useRef } from "react";
import {
  MDBIcon,
  MDBDropdown,
  MDBDropdownToggle,
  MDBDropdownMenu,
  MDBDropdownItem,
  MDBInputGroup,
  MDBInput,
  MDBCol,
  MDBRow,
  MDBPopover,
  MDBPopoverBody,
  MDBPopoverHeader,
  MDBSpinner, MDBBtn,
} from "mdb-react-ui-kit";
import { FilterMatchMode } from "primereact/api";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { format } from "date-fns";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ReactGA from "react-ga4";
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import fetchBillsWithHearings from "../../utils/fetchBillsWithHearings";
import fetchBillsWithSponsors from "../../utils/fetchBillsWithSponsors";
import fetchRelatedBills from "../../utils/fetchRelatedBills";
import fetchBillsWithAmendments from "../../utils/fetchBillsWithAmendments";
import fetchBillsWithExecutiveAction from "../../utils/fetchBillsWithExecutiveAction";

import "../../scss/datatable.scss";
import VoteHistory from "../Modals/VoteHistory";

function Bills({ id, name, isActive = true }) {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    fullBillNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    shortTitle: { value: null, matchMode: FilterMatchMode.CONTAINS },
    referredDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    sponsorFullName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    hearingDates: { value: null, matchMode: FilterMatchMode.CONTAINS },
    currentStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const dt = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [basicModal, setBasicModal] = useState(false);
  const [selectedVoteData, setSelectedVoteData] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Data fetching effect
  useEffect(() => {
    let mounted = true;

    const fetchBills = async () => {
      if (!isActive || hasLoaded) return;

      try {
        setLoading(true);
        const billsData = await fetchRelatedBills({committeeIds: [id]});
        
        if (!mounted) return;

        if (!billsData || billsData.length === 0) {
          setBills([]);
          setError("No Bills Referred.");
          return;
        }

        const billsWithHearings = await fetchBillsWithHearings(billsData);
        if (!mounted) return;

        const billsWithSponsorsAndHearings = await fetchBillsWithSponsors(
          billsWithHearings
        );
        if (!mounted) return;

        const billsWithAmendments = await fetchBillsWithAmendments(
          billsWithSponsorsAndHearings
        );
        if (!mounted) return;

        const billsWithExecutiveAction = await fetchBillsWithExecutiveAction(
          billsWithAmendments
        );
        if (!mounted) return;

        // PLEASE READ: Because of auditing issues, we are filtering out bills with incorrect double referrals using this logic here.  
        const filteredBills = billsWithExecutiveAction.filter(bill => {
          const statuses = bill.draft.billStatuses;
          const currentCommitteeId = parseInt(id);
          let shouldKeep = true;
          const committeeReferrals = statuses
            .map((status, index) => ({
              status,
              index,
              isCurrentCommittee: parseInt(status.standingCommitteeId) === currentCommitteeId,
              isReferral: status.billStatusCode.name.toLowerCase().includes('referred to committee') || status.billStatusCode.name.toLowerCase().includes('rereferred to committee')
            }))
            .filter(item => item.isCurrentCommittee && item.isReferral);
            committeeReferrals.forEach(({ index: referralIndex }) => {
            for (let i = referralIndex + 1; i < statuses.length; i++) {
              const status = statuses[i];
              if (status.billStatusCode.name.toLowerCase().includes('referred to committee') || status.billStatusCode.name.toLowerCase().includes('rereferred to committee')) {
                let foundHearing = false;
                for (let j = referralIndex + 1; j < i; j++) {
                  if (statuses[j].billStatusCode.name.toLowerCase().includes('hearing') &&
                      parseInt(statuses[j].standingCommitteeId) === currentCommitteeId) {
                    foundHearing = true;
                    break;
                  }
                }
                if (!foundHearing) {
                  shouldKeep = false;
                }
                break; 
              }
            }
          });
          return shouldKeep;
        });        
        const uniqueBills = [];
        for (const bill of filteredBills) {
          bill.fullBillNumber =
            bill.billType && bill.billNumber
              ? bill.billType.code + " " + bill.billNumber
              : " ";
          // Create a normalized version that lowercases and removes spaces, example: "HB 2" → "hb2"
          bill.normalizedFullBillNumber = bill.fullBillNumber
            .toLowerCase()
            .replace(/\s/g, "");
          bill.shortTitle = bill.draft?.shortTitle;
          bill.hasAmendments = bill.amendments?.length > 0;
          bill.ynAmendments = bill.hasAmendments ? "Has Amendments" : "No";
          bill.sponsorFullName = bill.sponsor
            ? bill.sponsor.lastName + ", " + bill.sponsor.firstName
            : " ";
          bill.hearingDetails = bill.hearingDetails.filter(
            (detail) => detail.committeeId === parseInt(id)
          );
          bill.hearingDates = bill.hearingDetails
            .reverse()
            .map((detail) =>
              format(new Date(detail.meetingTime), "MM/dd/yyyy, h:mm a")
            )
            .toString();
          bill.currentStatus =
            bill.draft.billStatuses.length > 0
              ? bill.draft.billStatuses[bill.draft.billStatuses.length - 1]
                  .billStatusCode.name
              : " ";

          bill.referredDate = "";
            if (bill.draft.billStatuses && bill.draft.billStatuses.length > 0) {
              const reversedStatuses = [...bill.draft.billStatuses].reverse();
              const referredStatus = reversedStatuses.find((status) => {
                const statusName = status.billStatusCode.name;
                return (
                  parseInt(status.standingCommitteeId) === parseInt(id) &&
                  (statusName.includes("Referred to Committee") ||
                  statusName.includes("Rereferred to Committee"))
                );
              });
              if (referredStatus) {
                bill.referredDate = new Date(referredStatus.timeStamp).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                });
              }
            }
          uniqueBills.push(bill);
        }
        if (mounted) {
          setBills(uniqueBills);
        }
      } catch (error) {
        if (mounted) {
          setError("Error fetching Bills data");
          console.error("Error fetching Bills data:", error);
        }
      } finally {
        if (mounted) {
          setLoading(false);
          setHasLoaded(true);
        }
      }
    };

    fetchBills();

    return () => {
      mounted = false;
    };
  }, [id, isActive, hasLoaded]);

  // Vote history modal effect
  useEffect(() => {
    const billId = searchParams.get('billId');
    if (billId && bills.length > 0) {
      const bill = bills.find(b => b.id === billId);
      if (bill) {
        const voteData = {
          id: bill.id,
          votes: bill.executiveAction,
          number: `${bill.billType.code} ${bill.billNumber}`,
          draftNumber: bill.draft.draftNumber,
          sessionId: bill.sessionId,
        };
        setSelectedVoteData(voteData);
        setBasicModal(true);
      }
    }
  }, [searchParams, bills]);

  const handleModalClose = () => {
    setBasicModal(false);
    navigate('.', { replace: true });
  };

  const openVoteHistory = (bill) => {
    const voteData = {
      id: bill.id,
      votes: bill.executiveAction,
      number: `${bill.billType.code} ${bill.billNumber}`,
      draftNumber: bill.draft.draftNumber,
      sessionId: bill.sessionId,
    };
    setSelectedVoteData(voteData);
    setBasicModal(true);
    navigate(`?billId=${bill.id}`);
  };

  // If the tab isn't active, render empty div instead of null
  if (!isActive) {
    return <div />;
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <MDBSpinner role="status" size="lg">
          <span className="visually-hidden">Loading...</span>
        </MDBSpinner>
      </div>
    );
  }

  if (error) return <div>{error}</div>;
  if (!bills.length) return <div className="mb-0">No bills found for this committee.</div>;

  const formatDatewithTime = (dateString) => {
    if (!dateString) return "";
    try {
      const parsedDate = new Date(dateString);

      // Check if parsedDate is a valid Date object
      if (isNaN(parsedDate)) return "Invalid date";

      // Format the valid date
      return format(parsedDate, "MM/dd/yyyy, h:mm a");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid date";
    }
  };

  // Global filter input onChange handler
  // ormalize the value (lowercase and remove spaces) before updating filters.
  const onGlobalFilterChange = (e) => {
    const rawValue = e.target.value;
    // Save the raw value in the input field state
    setGlobalFilterValue(rawValue);
    // Normalize the filter input so that "hb2" and "hb 2" both become "hb2"
    const normalizedValue = rawValue.toLowerCase().replace(/\s/g, "");
    let _filters = { ...filters };
    _filters["global"].value = normalizedValue;
    setFilters(_filters);
  };

  // Export
  const exportCSV = (selectionOnly) => {
    try {
      ReactGA.event({
        category: "User Interaction",
        action: "CSV download",
        label: "Download",
      });

      setIsExporting(true);
      const committeeName = name || "Committee";

      // Format the filename
      let sanitizedCommitteeName = committeeName.replace(/[^a-zA-Z0-9]/g, "-"); // Replace special characters with underscores
      sanitizedCommitteeName = sanitizedCommitteeName.replace(/--+/g, "-"); // Replace multiple dashes with a single dash

      // Remove the first "-" if it exists
      if (sanitizedCommitteeName.startsWith("-")) {
        sanitizedCommitteeName = sanitizedCommitteeName.substring(1);
      }
      dt.current.props.exportFilename = `${sanitizedCommitteeName}-Bills.csv`;

      dt.current.exportCSV({
        selectionOnly,
        selectionOnlyReturn: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportPdf = async () => {
    try {
      ReactGA.event({
        category: "User Interaction",
        action: "PDF download",
        label: "Download",
      });

      setIsExporting(true);
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4",
      });

      const committeeName = name || "Committee";
      const tableData = dt.current.getTable();

      // Define margin values (36 points = 0.5 inch)
      const margin = 36;
      const pageWidth = doc.internal.pageSize.width;
      const logoPath = "/logo192.png";

      const convertToBase64 = async (path) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.src = path;
          img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png"));
          };
          img.onerror = reject;
        });
      };

      const logoBase64 = await convertToBase64(logoPath);
      const logoWidth = 40;
      const logoHeight = 40;

      doc.addImage(logoBase64, "PNG", margin, 20, logoWidth, logoHeight);

      doc.setFontSize(12);
      doc.text("69th Legislature 2025", pageWidth / 2, margin, {
        align: "center",
      });
      doc.setFontSize(16);
      doc.text(`Committee: ${committeeName}`, pageWidth / 2, margin + 20, {
        align: "center",
      });

      // Generate the table
      doc.autoTable({
        html: tableData,
        startY: margin + 30,
        headStyles: { fillColor: [103, 137, 127] },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        styles: { fontSize: 8 },
      });

      // Format the filename
      let sanitizedCommitteeName = committeeName.replace(/[^a-zA-Z0-9]/g, "-"); // Replace special characters with dashes
      sanitizedCommitteeName = sanitizedCommitteeName.replace(/--+/g, "-"); // Replace multiple dashes with a single dash

      // Remove the first "-" if it exists
      if (sanitizedCommitteeName.startsWith("-")) {
        sanitizedCommitteeName = sanitizedCommitteeName.substring(1);
      }

      const pageCount = doc.internal.getNumberOfPages();
      const printedOn = new Date().toLocaleString([], {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(100);

        // Date printed
        doc.text(`Printed: ${printedOn}`, margin, doc.internal.pageSize.height - 20);

        // Page X of Y
        const pageLabel = `Page ${i} of ${pageCount}`;
        doc.text(pageLabel, pageWidth - margin - doc.getTextWidth(pageLabel), doc.internal.pageSize.height - 20);
      }

      // Create the file name
      const fileName = `${sanitizedCommitteeName}-Bills.pdf`;

      // Save the file
      doc.save(fileName);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <MDBRow className="g-1">
        <MDBCol size="lg">
          <MDBInputGroup
            className="mb-3"
            noBorder
            textBefore={<MDBIcon fas icon="search" />}
          >
            <MDBInput
              type="text"
              label="Search Bills"
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
            />
          </MDBInputGroup>
        </MDBCol>
        <MDBCol size="auto" className="align-items-center">
          <MDBPopover
            tag="a"
            color="none"
            className="ms-3"
            btnChildren={<i className="fas fa-info-circle"></i>}
            style={{ cursor: "pointer" }}
          >
            <MDBPopoverHeader>Search Tips</MDBPopoverHeader>
            <MDBPopoverBody>
              Type "Has Amendments" to see only bills with ammendments.
            </MDBPopoverBody>
          </MDBPopover>
        </MDBCol>
        <MDBCol size="auto">
          <MDBDropdown className="mb-3 mb-lg-0">
            <MDBDropdownToggle>
              {isExporting ? (
                <MDBSpinner size="sm" role="status" />
              ) : (
                <MDBIcon fas icon="cloud-download-alt" />
              )}{" "}
              Export
            </MDBDropdownToggle>
            <MDBDropdownMenu>
              <MDBDropdownItem link childTag="button" onClick={exportPdf}>
                Print/PDF
              </MDBDropdownItem>
              <MDBDropdownItem
                link
                childTag="button"
                onClick={() => exportCSV(false)}
              >
                Excel/CSV
              </MDBDropdownItem>
            </MDBDropdownMenu>
          </MDBDropdown>
        </MDBCol>
      </MDBRow>
      <DataTable
        ref={dt}
        value={bills}
        showGridlines
        stripedRows
        resizableColumns
        paginator
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        rows={100}
        rowsPerPageOptions={[25, 50, 100]}
        scrollable
        scrollHeight="500px"
        sortField="fullBillNumber"
        sortOrder={1}
        removableSort
        loading={loading}
        filters={filters}
        globalFilterFields={[
          "normalizedFullBillNumber",
          "shortTitle",
          "ynAmendments",
          "referredDate",
          "hearingDates",
          "sponsorFullName",
          "currentStatus",
          "executiveAction",
        ]}
        // Pass the already normalized value to the globalFilter prop.
        globalFilter={filters.global.value}
        emptyMessage="No results found."
      >
        <Column
          field="fullBillNumber"
          header="Bill #"
          body={(rowData) => (
            <a
              href={`${process.env.REACT_APP_BILLS_EXPLORER}/#/laws/bill/${rowData.sessionId}/${rowData.draft.draftNumber}`}
              target="_blank"
              title="View Bill"
            >
              {rowData.billType.code} {rowData.billNumber}
            </a>
          )}
          sortField="fullBillNumber"
          sortable
          filter
          filterField="fullBillNumber"
          filterPlaceholder="Search Bill #"
        />
        <Column
          field="shortTitle"
          header="Short Title"
          body={(rowData) => (
            <span
              className="short-title"
              title={rowData.draft?.shortTitle}
              style={{ whiteSpace: "normal", wordWrap: "break-word" }}
            >
              {rowData.draft?.shortTitle || " "}
              {rowData.ynAmendments === "Has Amendments" && (
                <a
                  href={`${process.env.REACT_APP_BILLS_EXPLORER}/#/laws/bill/${rowData.sessionId}/${rowData.draft.draftNumber}?open_tab=amend`}
                  target="_blank"
                  title="View Amendment(s)"
                  style={{ marginLeft: "8px" }}
                >
                  <i className="fa-solid fa-square-a"></i>
                </a>
              )}
            </span>
          )}
          sortField="shortTitle"
          sortable
          filter
          filterField="shortTitle"
          filterPlaceholder="Search Short Title"
        />
        <Column
          field="ynAmendments"
          header="Amendment(s)"
          body={(rowData) => {
            const amendments = rowData?.amendments;
            return amendments && amendments.length > 0 ? (
              <a
                href={`${process.env.REACT_APP_BILLS_EXPLORER}/#/laws/bill/${rowData.sessionId}/${rowData.draft.draftNumber}?open_tab=amend`}
                target="_blank"
                title="View Amendment"
              >
                Yes
              </a>
            ) : (
              <>No</>
            );
          }}
          sortField="ynAmendments"
          sortable
          filterField="ynAmendments"
          filterPlaceholder="Search Amendments"
          filterMatchMode="contains"
          filter={(value, rowData) => {
            if (!value) return true;
            const searchTerm = value.toLowerCase();
            return (
              rowData.ynAmendments.toLowerCase().includes(searchTerm) ||
              (searchTerm === "amendments" &&
                rowData.ynAmendments.toLowerCase() === "yes")
            );
          }}
          hidden
        />
        <Column
          field="referredDate"
          header="Referred"
          body={(rowData) => rowData.referredDate}
          sortField="referredDate"
          sortable
          filter
          filterField="referredDate"
          filterPlaceholder="Search Referred Date"
        />
        <Column
          field="hearingDates"
          header="Hearing"
          body={(rowData) =>
            rowData.hearingDetails.length
              ? rowData.hearingDetails.map((detail, index) => (
                  <div key={index}>
                    {formatDatewithTime(detail.meetingTime)}
                  </div>
                ))
              : ""
          }
          sortField="hearingDates"
          sortable
          filter
          filterField="hearingDates"
          filterPlaceholder="Search Hearing Date & Time"
        />
        <Column
          field="sponsorFullName"
          header="Sponsor"
          body={(rowData) =>
            rowData.sponsor
              ? `${rowData.sponsor.lastName}, ${rowData.sponsor.firstName}`
              : " "
          }
          sortField="sponsorFullName"
          sortable
          filter
          filterField="sponsorFullName"
          filterPlaceholder="Search Sponsor"
        />
        <Column
          field="currentStatus"
          header="Current Status"
          body={(rowData) => (
            <span
              className="bill-statuses"
              style={{ whiteSpace: "normal", wordWrap: "break-word" }}
            >
              {
                rowData.draft.billStatuses[
                  rowData.draft.billStatuses.length - 1
                ]?.billStatusCode?.name
              }
            </span>
          )}
          sortField="currentStatus"
          sortable
          filter
          filterField="currentStatus"
          filterPlaceholder="Search Current Status"
        />
        <Column
          header="Votes"
          body={(rowData) => (
              rowData.executiveAction && rowData.executiveAction.length > 0 ?
                  <MDBBtn
                      tag="a"
                      color="none"
                      title="Vote History"
                      onClick={() => openVoteHistory(rowData)}
                  >
                    <i className="fa-light fa-check-to-slot"></i>
                  </MDBBtn> : null
          )}
          bodyStyle={{ textAlign: "center" }}
          headerClassName="text-center"
        />
      </DataTable>
        {basicModal && (
        <VoteHistory
            basicModal={basicModal}
            setBasicModal={handleModalClose}
            billId={selectedVoteData.id}
            draftNumber={selectedVoteData.draftNumber}
            sessionId={selectedVoteData.sessionId}
        />
        )}
      </>
  );
}

export default Bills;
